import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import { FileText, ArrowUpRight, ArrowDownRight, Clock } from 'lucide-react';

function TransactionHistory() {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const { currentUser } = useAuth();
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Fetch transactions for the current user
  useEffect(() => {
    const fetchTransactions = async () => {
      if (currentUser) {
        setLoading(true);
        const transactionsQuery = query(
          collection(db, 'Transactions'),
          where('user_id', '==', currentUser.uid)
        );
        const querySnapshot = await getDocs(transactionsQuery);
        const transactionsList = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data()
        }));

        // Sort by date (descending)
        transactionsList.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        setTransactions(transactionsList);
        setLoading(false);
      }
    };

    fetchTransactions();
  }, [currentUser]);

  // Helper function to get transaction icon
  const getTransactionIcon = (description, amount) => {
    if (description.includes('Deposit') || description.includes('Add')) {
      return <ArrowDownRight className="w-5 h-5 text-emerald-400" />;
    } else if (description.includes('Withdrawal') || description.includes('Withdraw')) {
      return <ArrowUpRight className="w-5 h-5 text-red-400" />;
    } else if (parseFloat(amount) > 0) {
      return <ArrowDownRight className="w-5 h-5 text-emerald-400" />;
    } else {
      return <ArrowUpRight className="w-5 h-5 text-red-400" />;
    }
  };

  return (
    <div className="min-h-screen">
      <h2 className="text-2xl font-bold text-white mb-6 flex items-center">
        <FileText className="w-6 h-6 text-emerald-400 mr-2" />
        Transaction History
      </h2>

      {loading ? (
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-emerald-500"></div>
          <span className="ml-3 text-white">Loading transactions...</span>
        </div>
      ) : transactions.length > 0 ? (
        isMobile ? (
          // Mobile view - card layout
          <div className="space-y-4">
            {transactions.map((transaction) => (
              <div 
                key={transaction.id} 
                className="bg-gray-800/50 backdrop-blur-sm rounded-xl border border-gray-700 hover:border-emerald-500/30 transition-all duration-300 p-4"
              >
                <div className="flex justify-between items-center mb-2">
                  <div className="flex items-center">
                    <div className="bg-gray-700/50 p-2 rounded-full mr-3">
                      {getTransactionIcon(transaction.description, transaction.amount)}
                    </div>
                    <div>
                      <h3 className="font-medium text-white">{transaction.description}</h3>
                      <p className="text-xs text-gray-400">
                        {new Date(transaction.createdAt).toLocaleDateString()} at {new Date(transaction.createdAt).toLocaleTimeString()}
                      </p>
                    </div>
                  </div>
                  <span className={`font-bold ${parseFloat(transaction.amount) >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                    {parseFloat(transaction.amount) >= 0 ? '+' : ''}{parseFloat(transaction.amount).toFixed(2)}
                  </span>
                </div>
                <div className="border-t border-gray-700 my-2 pt-2 text-sm">
                  <div className="flex justify-between text-gray-400">
                    <span>Before:</span>
                    <span>${parseFloat(transaction.beforeBalance).toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-gray-400">
                    <span>After:</span>
                    <span>${parseFloat(transaction.afterBalance).toFixed(2)}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          // Desktop view - table layout
          <div className="bg-gray-900/50 backdrop-blur-sm rounded-xl border border-gray-800 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead>
                  <tr className="bg-gray-800/70 text-gray-300 text-left">
                    <th className="py-3 px-4 font-medium">Date</th>
                    <th className="py-3 px-4 font-medium">Description</th>
                    <th className="py-3 px-4 font-medium">Amount</th>
                    <th className="py-3 px-4 font-medium">Before Balance</th>
                    <th className="py-3 px-4 font-medium">After Balance</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-700">
                  {transactions.map((transaction) => (
                    <tr key={transaction.id} className="hover:bg-gray-800/30 transition-colors">
                      <td className="py-3 px-4 text-gray-300">
                        {new Date(transaction.createdAt).toLocaleDateString()}
                      </td>
                      <td className="py-3 px-4 text-white">
                        <div className="flex items-center">
                          <div className="bg-gray-700/50 p-1.5 rounded-full mr-2">
                            {getTransactionIcon(transaction.description, transaction.amount)}
                          </div>
                          {transaction.description}
                        </div>
                      </td>
                      <td className={`py-3 px-4 font-medium ${parseFloat(transaction.amount) >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                        {parseFloat(transaction.amount) >= 0 ? '+' : ''}${Math.abs(parseFloat(transaction.amount)).toFixed(2)}
                      </td>
                      <td className="py-3 px-4 text-gray-300">${parseFloat(transaction.beforeBalance).toFixed(2)}</td>
                      <td className="py-3 px-4 text-gray-300">${parseFloat(transaction.afterBalance).toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )
      ) : (
        <div className="text-center py-12 bg-gray-800/30 rounded-xl border border-gray-700">
          <Clock className="w-12 h-12 text-gray-500 mx-auto mb-3" />
          <p className="text-gray-300">No transactions found</p>
          <p className="text-gray-400 text-sm mt-2">Your transaction history will appear here</p>
        </div>
      )}
    </div>
  );
}

export default TransactionHistory;
